import { HTMLTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { useOverviewContext } from "@features/overview-v2/hooks";
import { TileType, type TileDescriptor } from "@features/overview-v2/types";
import { type SalesPageMetric } from "@north-beam/nb-client-side";
import {
  MCAttributionWindowDays,
  type IMCAttributionWindowDays,
} from "@north-beam/nb-common";
import { toClicksAndViewsLabel } from "@pages/sales/report-body-control/attribution-window-select";

interface Props {
  metric1: SalesPageMetric;
  metric2?: SalesPageMetric;
  tile: TileDescriptor;
}

export const SubText = ({ metric1, metric2, tile }: Props) => {
  const { filters } = useOverviewContext();
  const { user } = useUser();

  const amEnum = user.attributionMethodEnum;

  const prettyAttributionModel = amEnum.reactSelectOptions.find(
    (i) => i.value === filters.attributionModel,
  );

  let attributionWindowText = prettyAttributionWindow({
    attributionModel: filters.attributionModel,
    attributionWindow: filters.attributionWindow,
  });

  if (filters.accountingMode === "cash") {
    attributionWindowText = "cash";
  }

  if (tile.type === TileType.CONVERSION_LAG) {
    attributionWindowText = "";
  }

  if (!metric2) {
    return (
      <div className="px-6 text-xs text-subtitle">
        Visualizing{" "}
        {tile.type === TileType.CONVERSION_LAG ? "conversion lag for " : ""}
        <HTMLTooltip
          noInfoCircle
          className="underline"
          html={metric1.descriptionHTML}
        >
          {metric1.name.toLowerCase()}
        </HTMLTooltip>{" "}
        on a {attributionWindowText}{" "}
        {prettyAttributionModel?.label?.toLowerCase()} basis
      </div>
    );
  }
  return (
    <div className="px-6 text-xs text-subtitle">
      Visualizing{" "}
      <HTMLTooltip
        noInfoCircle
        className="underline"
        html={metric1.descriptionHTML}
      >
        {metric1.name.toLowerCase()}
      </HTMLTooltip>{" "}
      vs{" "}
      <HTMLTooltip
        noInfoCircle
        className="underline"
        html={metric2.descriptionHTML}
      >
        {metric2.name.toLowerCase()}
      </HTMLTooltip>{" "}
      on a{" "}
      {prettyAttributionWindow({
        attributionModel: filters.attributionModel,
        attributionWindow: filters.attributionWindow,
      })}{" "}
      {prettyAttributionModel?.label?.toLowerCase()} basis
    </div>
  );
};

function prettyAttributionWindow({
  attributionWindow,
  attributionModel,
}: {
  attributionWindow: IMCAttributionWindowDays;
  attributionModel: string;
}) {
  let value = MCAttributionWindowDays.toReactSelectUnsafe(attributionWindow);

  if (attributionModel === "northbeam_custom__va") {
    value = toClicksAndViewsLabel(value);
  }

  return value.label;
}
